/**
 * Centralized Query Keys
 * Single source of truth for all React Query cache keys
 * Prevents typos and makes cache invalidation easier
 *
 * Pattern:
 * - Use arrays for hierarchical keys: ['conversations', conversationId]
 * - Use 'as const' for type safety
 * - Group by feature/domain
 */
const CONVERSATION_ROOT = ["conversations"] as const;

export const queryKeys = {
  /** Session & auth keys */
  session: {
    all: ["session"] as const,
    status: () => [...queryKeys.session.all, "status"] as const,
    user: (userId: string) =>
      [...queryKeys.session.all, "user", userId] as const,
  },

  /** Canonical conversation keys */
  conversations: {
    all: CONVERSATION_ROOT,
    lists: () => [...queryKeys.conversations.all, "list"] as const,
    list: (filters?: Record<string, any>) =>
      [...queryKeys.conversations.lists(), filters] as const,
    details: () => [...queryKeys.conversations.all, "detail"] as const,
    detail: (conversationId: string) =>
      [...queryKeys.conversations.details(), conversationId] as const,
    objects: (conversationId: string) =>
      [...queryKeys.conversations.detail(conversationId), "objects"] as const,
    summary: (conversationId: string) =>
      [...queryKeys.conversations.detail(conversationId), "summary"] as const,
    /**
     * Server-assembled ConversationJson snapshot (#1282).
     * Keyed by workspace + conversation to preserve cache partitioning across
     * workspaces (a conversation id is unique per workspace, but the key was
     * historically a `[root, workspaceId, conversationId]` tuple — keep both).
     */
    json: (workspaceId: string | null, conversationId: string | null) =>
      [
        ...queryKeys.conversations.detail(conversationId ?? ""),
        "json",
        workspaceId,
      ] as const,
    activity: (conversationId: string) =>
      [...queryKeys.conversations.detail(conversationId), "activity"] as const,
    /**
     * First-paint persisted module-pane resolution (AuthenticatedAppShell Gate 6).
     * A SIBLING of `detail` (not nested under it) so the frequent
     * `invalidateQueries(detail(id))` calls don't prefix-match and needlessly
     * refetch this one-shot read.
     */
    initialPane: (conversationId: string) =>
      [...queryKeys.conversations.all, "initial-pane", conversationId] as const,
  },

  /** Workspace module keys */
  modules: {
    all: ["modules"] as const,
    list: (workspaceId: string) =>
      [...queryKeys.modules.all, "list", workspaceId] as const,
    sidebarPreferences: (workspaceId: string) =>
      [...queryKeys.modules.all, "sidebarPreferences", workspaceId] as const,
  },

  /** Object keys */
  objects: {
    all: ["objects"] as const,
    lists: () => [...queryKeys.objects.all, "list"] as const,
    list: (filters?: Record<string, any>) =>
      [...queryKeys.objects.lists(), filters] as const,
    details: () => [...queryKeys.objects.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.objects.details(), id] as const,
    byId: (id: string) => [...queryKeys.objects.detail(id)] as const,
    byType: (type: string) =>
      [...queryKeys.objects.all, "byType", type] as const,
  },

  /** Chat keys */
  chat: {
    all: ["chat"] as const,
    history: (userId: string) =>
      [...queryKeys.chat.all, "history", userId] as const,
    messages: (sessionId: string) =>
      [...queryKeys.chat.all, "messages", sessionId] as const,
  },

  /** Search keys */
  search: {
    all: ["search"] as const,
    hybrid: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, "hybrid", query, filters] as const,
    semantic: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, "semantic", query, filters] as const,
    agent: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, "agent", query, filters] as const,
  },

  /** User settings */
  user: {
    all: ["user"] as const,
    session: () => [...queryKeys.user.all, "session"] as const,
    settings: () => [...queryKeys.user.all, "settings"] as const,
  },

  /** Graph/visualization keys */
  graph: {
    all: ["graph"] as const,
    visualization: (filters?: Record<string, any>) =>
      [...queryKeys.graph.all, "visualization", filters] as const,
  },

  /** Agent/tools keys */
  agent: {
    all: ["agent"] as const,
    config: () => [...queryKeys.agent.all, "config"] as const,
    query: (queryId: string) =>
      [...queryKeys.agent.all, "query", queryId] as const,
    enrichPlace: (placeId: string) =>
      [...queryKeys.agent.all, "enrichPlace", placeId] as const,
  },

  /** File upload keys */
  files: {
    all: ["files"] as const,
    upload: (jobId: string) =>
      [...queryKeys.files.all, "upload", jobId] as const,
  },

  /** Card keys */
  cards: {
    all: ["cards"] as const,
    byId: (cardId: string) => [...queryKeys.cards.all, cardId] as const,
    byObject: (objectId: string) =>
      [...queryKeys.cards.all, "object", objectId] as const,
  },

  /** Activities panel keys */
  activities: {
    all: (workspaceId: string) => ["activities", workspaceId] as const,
    tab: (workspaceId: string, tab: string) =>
      [...queryKeys.activities.all(workspaceId), tab] as const,
    badge: (workspaceId: string) =>
      [...queryKeys.activities.all(workspaceId), "badge"] as const,
  },

  /** Drive sync keys */
  driveSync: {
    status: (workspaceId?: string) =>
      ["driveSync", "status", workspaceId] as const,
    details: () => ["driveSync", "details"] as const,
    verify: () => ["driveSync", "verify"] as const,
  },

  /** Google integration keys */
  googleAccounts: {
    all: ["google-accounts"] as const,
    byModule: (moduleId: string, conversationId: string) =>
      ["google-accounts", moduleId, conversationId] as const,
  },

  /** Google Drive browse keys */
  gdriveBrowse: {
    all: ["gdriveBrowse"] as const,
    lists: () => [...queryKeys.gdriveBrowse.all, "list"] as const,
    list: (
      parentId: string | null,
      pageToken: string | null,
      accountId?: string | null,
    ) =>
      [
        ...queryKeys.gdriveBrowse.lists(),
        accountId ?? "default",
        parentId ?? "root",
        pageToken ?? "first",
      ] as const,
  },

  /** Email Intelligence Report keys */
  emailIntelligence: {
    all: ["emailIntelligence"] as const,
    report: (ws: string) =>
      [...queryKeys.emailIntelligence.all, "report", ws] as const,
    coverage: (ws: string) =>
      [...queryKeys.emailIntelligence.all, "coverage", ws] as const,
  },
  /** Agent jobs (Agents module) keys */
  agentJobs: {
    all: ["agentJobs"] as const,
    list: (workspaceId: string) =>
      [...queryKeys.agentJobs.all, "list", workspaceId] as const,
    detail: (id: string) => [...queryKeys.agentJobs.all, "detail", id] as const,
  },

  /** Agent trust (Agents redesign): roster, sign-off queue, grants */
  agentTrust: {
    all: ["agentTrust"] as const,
    roster: (workspaceId: string) =>
      [...queryKeys.agentTrust.all, "roster", workspaceId] as const,
    approvals: (workspaceId: string) =>
      [...queryKeys.agentTrust.all, "approvals", workspaceId] as const,
    grants: (agentId: string) =>
      [...queryKeys.agentTrust.all, "grants", agentId] as const,
  },

  /** Synthesis module keys */
  synthesis: {
    all: ["synthesis"] as const,
    byWorkspace: (workspaceId: string) =>
      [...queryKeys.synthesis.all, workspaceId] as const,
    /** Per-tab cache key (people, work, history, travel, health, watchlist, overview). */
    tab: (workspaceId: string, tab: string) =>
      [...queryKeys.synthesis.all, workspaceId, tab] as const,
  },

  /** Contacts outbound sync (two-way Google Contacts) */
  contactsOutbound: {
    all: ["contactsOutbound"] as const,
    byIntegration: (integrationId: string) =>
      [...queryKeys.contactsOutbound.all, integrationId] as const,
  },

  /** Scope-aware capability statuses for an integration (ADR 017 D4). The sole
   *  consumer of GET /integrations/:id/capabilities — drives <ScopeUpgradePrompt>. */
  integrationCapabilities: {
    all: ['integrationCapabilities'] as const,
    byId: (integrationId: string) =>
      [...queryKeys.integrationCapabilities.all, integrationId] as const,
  },

  /** A workspace's integrations list (GET /workspaces/:ws/integrations) — the
   *  provider-agnostic V1+V2 union of every connected account. Drives the
   *  capability-enable UI (ADR 017 D4 / U2). */
  integrations: {
    all: ['integrations'] as const,
    list: (workspaceId: string) =>
      [...queryKeys.integrations.all, 'list', workspaceId] as const,
  },

  /** Contact candidate buckets (Potential / Flagged) */
  contactCandidates: {
    potential: (includeOneWay: boolean) =>
      ["contactCandidates", "potential", includeOneWay] as const,
    flagged: () => ["contactCandidates", "flagged"] as const,
  },
} as const;

/**
 * Helper to invalidate all queries for a specific conversation
 */
export function invalidateConversation(
  queryClient: any,
  conversationId: string,
) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.conversations.detail(conversationId),
  });
}

/**
 * Helper to invalidate all queries for a specific object
 */
export function invalidateObject(queryClient: any, objectId: string) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.objects.detail(objectId),
  });
}

/**
 * Helper to invalidate all search queries
 */
export function invalidateSearch(queryClient: any) {
  queryClient.invalidateQueries({ queryKey: queryKeys.search.all });
}
